/**
 * Turning an assessed luxury tax into money that has actually left a GM's FAAB.
 *
 * THE TAX HAS ALWAYS BEEN A FIGURE ON A CARD AND NEVER A LEDGER ROW. `assessLuxuryTax` prices a
 * slate, every screen that shows a slate shows the price, and Home even says "₣140 now, ₣96 once
 * the draft makes them official" — a sentence about a charge that nothing in the app was able to
 * make. Somebody moved the money by hand, or nobody did. Tim, 3 September: "once keepers lock,
 * you can charge FAAB to GMs."
 *
 * WHY IT WAITS FOR THE LOCK. Before the deadline a slate is a draft in the ordinary sense: a GM
 * adds a man, drops him, tags somebody else, and the tax moves with every one of those. Charging
 * against a moving figure means either a ledger full of corrections or a balance that is wrong
 * between them. The lock is the moment the figure stops moving, which is the moment it becomes a
 * debt. `chargeable` is that gate and it takes the same `Gate` the declare screen runs on, so the
 * two cannot disagree about whether keepers are shut.
 *
 * A CORRECTION IS A NEW ROW, NOT A REWRITE, AND THAT IS THE LEDGER'S RULE RATHER THAN A CHOICE
 * HERE. `firestore.rules` says `allow create: if isCommish(); allow update, delete: if false` on
 * `faab`, deliberately: this is "a running ledger rather than a balance, because the balance is
 * contested often enough that the audit trail matters more than the number". So a slate corrected
 * under a commissioner's unlock exception produces a second row carrying the difference, and what
 * a GM owes is the SUM of his rows. Nothing is ever overwritten and February can still see what
 * happened in August.
 *
 * THE ID IS THE SEQUENCE, WHICH IS WHAT MAKES A DOUBLE-PRESS SAFE. `tax-2026-TIM-1`, then `-2`.
 * Derived from how many tax rows the man already has, so pressing twice against the same ledger
 * computes the same id twice and the second create is REFUSED by the rules rather than writing a
 * duplicate charge. A sequence rather than the amount because a slate that goes 44, 88 and back to
 * 44 would otherwise collide with its own first row.
 */

import type { FaabEntry, GmId } from '../store/types.js';

/** Every luxury-tax row in the ledger is this kind, and nothing else writes it. */
export const TAX_KIND = 'luxury-tax' as const;

/** Season, GM and which tax row of his this is. See the note above on why it is the sequence. */
export function chargeId(season: number, gm: string, seq: number): string {
  return `tax-${season}-${gm.toUpperCase()}-${seq}`;
}

export interface Assessed {
  gm: GmId;
  /** What the slate is priced at. Zero for a GM inside his free keepers. */
  tax: number;
  /** He sent a slate. A slate that exists only because the rules put a graduate on it has not. */
  submitted: boolean;
}

export interface ChargeRow {
  gm: GmId;
  /** What the rules say he owes now. */
  owed: number;
  /** What the ledger has already taken off him under `luxury-tax`, summed across his rows. */
  already: number;
  /** The write that would put the ledger right. Never zero in a returned row. */
  delta: number;
  /** Which tax row of his this would be, 1-based. The id is built from it. */
  seq: number;
}

/**
 * What the ledger is missing, GM by GM.
 *
 * A GM appears only where the ledger disagrees with the assessment, so a second run after a
 * successful first returns nothing and the button that calls it has nothing to offer.
 *
 * AN UNSUBMITTED SLATE IS NOT A DEBT. He declared nothing, so the rules charge him nothing, and a
 * row that charged him for the graduate the rules forced onto his slate would be the app inventing
 * a bill. Where such a GM has somehow been charged, the correction back to zero IS returned —
 * money taken in error is the one case worth writing.
 */
export function taxCharges(
  assessed: readonly Assessed[],
  ledger: readonly FaabEntry[],
  season: number,
): ChargeRow[] {
  const paid = new Map<string, number>();
  const rows = new Map<string, number>();
  for (const e of ledger) {
    if (e.kind !== TAX_KIND || e.season !== season) continue;
    const who = e.gm.toUpperCase();
    /* Spends are negative in this ledger; the debt is the positive of them. */
    paid.set(who, (paid.get(who) ?? 0) - e.amount);
    rows.set(who, (rows.get(who) ?? 0) + 1);
  }

  const out: ChargeRow[] = [];
  for (const a of assessed) {
    const gm = a.gm.toUpperCase() as GmId;
    const owed = a.submitted ? Math.max(0, Math.round(a.tax)) : 0;
    const already = Math.round(paid.get(gm) ?? 0);
    if (owed === already) continue;
    out.push({ gm, owed, already, delta: owed - already, seq: (rows.get(gm) ?? 0) + 1 });
  }
  return out.sort((a, b) => a.gm.localeCompare(b.gm));
}

/**
 * The rows as ledger entries.
 *
 * The amount written is the DELTA, because the ledger appends: a man already charged ₣44 whose
 * slate now prices at ₣88 gets a second row of ₣44, and the two together say he owes ₣88. A
 * correction downwards is a positive amount, which is a credit, and reads as one.
 *
 * The description names the season, since "luxury tax" alone on a line read in February does not
 * say what it was a tax on, and a correction says what it is correcting TO — the figure a reader
 * wants off a correction is the new total, not the difference he can already see in the amount.
 */
export function chargeEntries(
  rows: readonly ChargeRow[],
  season: number,
  by: GmId,
  at: string,
): FaabEntry[] {
  return rows.map((r) => ({
    id: chargeId(season, r.gm, r.seq),
    season,
    gm: r.gm,
    /*
     * Negative is a spend. `-r.delta` because `delta` is what he still owes: owing more is a
     * bigger spend, owing less is a credit back. The zero case cannot arise — `taxCharges` never
     * returns a row whose delta is zero — so there is no `-0` to write.
     */
    amount: -r.delta,
    kind: TAX_KIND,
    description: r.already === 0
      ? `${season} luxury tax`
      : `${season} luxury tax — corrected to ${r.owed === 0 ? 'nil' : `₣${r.owed}`}`,
    date: at,
    recordedBy: by,
  }));
}

/**
 * May the tax be charged at all?
 *
 * Keepers have to be shut. `gateOpen` is `gateFor(...).open` from the declare screen, passed in
 * rather than recomputed so there is one answer to "are declarations closed" in the app.
 */
export function chargeable(gateOpen: boolean): boolean {
  return !gateOpen;
}

/** The figure on the button: what this run moves, and how many GMs it touches. */
export function chargeSummary(rows: readonly ChargeRow[]): { total: number; gms: number } {
  return {
    total: rows.reduce((n, r) => n + r.delta, 0),
    gms: rows.length,
  };
}
